import { useState, useEffect } from 'react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useRestaurantContext } from '@/contexts/RestaurantContext';
import { useCurrentAssistant } from '@/hooks/useAssistantAuth';
import { WithPermission } from '@/components/auth/WithPermission';
import type { AssistantPermissions } from '@/types/entities';
import {
  useMenuItems,
  useCreateMenuItem,
  useUpdateMenuItem,
  useDeleteMenuItem,
  useToggleMenuItemAvailability,
  useMenuItemWithIngredients,
} from '@/hooks/useMenuItems';
import { useIngredients } from '@/hooks/useIngredients';
import { useInventory } from '@/hooks/useInventory';
import {
  useMenuItemOptions,
  useCreateMenuItemOption,
  useUpdateMenuItemOption,
  useDeleteMenuItemOption,
} from '@/hooks/useMenuItemOptions';
import {
  useMenuCategories,
  useCreateMenuCategory,
  useUpdateMenuCategory,
  useDeleteMenuCategory,
} from '@/hooks/useMenuCategories';
import {
  useKitchenSpecialties,
  useMenuItemSpecialties,
  useSetMenuItemSpecialties,
} from '@/hooks/useKitchenSpecialties';
import { useAllMenuItemSpecialties, type MenuItemSpecialty } from '@/hooks/useAllMenuItemSpecialties';
import { getCompatibleUnits } from '@/lib/unit-utils';
import { formatPrice } from '@/lib/utils';
import { applyLanguageFallback } from '@/lib/language-fallback';
import { Header } from '@/components/layout/Header';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Plus,
  Pencil,
  Trash2,
  CheckCircle,
  XCircle,
  X,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Settings,
  Camera,
  ArrowUp,
  ArrowDown,
  BarChart3,
} from 'lucide-react';
import type { MenuItem, Ingredient, MenuItemOption } from '@/types/entities';
import type { MenuItemIngredientInput, MeasurementUnit } from '@/types/api.types';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { AlertModal } from '@/components/ui/alert-modal';
import { IngredientIcon } from '@/components/ui/ingredient-icon';
import { ConfirmModal } from '@/components/ui/confirm-modal';
import { ImageUpload } from '@/components/common/ImageUpload';
import { MenuOCRImport } from '@/components/menu/MenuOCRImport';
import { IngredientsGraphDialog } from '@/components/menu/IngredientsGraphDialog';

// Component to display specialty badges for a menu item
// Now accepts pre-loaded specialties from batch query instead of fetching per-item
function MenuItemSpecialtiesBadges({ specialties }: { specialties: MenuItemSpecialty[] }) {
  const { i18n } = useTranslation();

  if (!specialties || specialties.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-wrap gap-1 sm:gap-2 mb-2 sm:mb-3">
      {specialties.map((specialty) => (
        <Badge
          key={specialty.specialtyId}
          style={{ backgroundColor: specialty.color || '#888' }}
          className="text-white text-xs"
        >
          {i18n.language === 'ar' ? specialty.nameAr : specialty.nameFr}
        </Badge>
      ))}
    </div>
  );
}

export function Menu() {
  const { t, i18n } = useTranslation();
  const { selectedRestaurantId } = useRestaurantContext();
  const { data: assistant } = useCurrentAssistant();
  const { data: menuItems, isLoading, refetch } = useMenuItems(selectedRestaurantId);
  // Batch load all menu item specialties - eliminates N+1 queries
  const { data: allSpecialtiesMap } = useAllMenuItemSpecialties(selectedRestaurantId);

  // Helper to conditionally wrap with permission check
  const ConditionalPermission = ({
    page,
    action,
    children,
  }: {
    page: keyof AssistantPermissions;
    action: string;
    children: React.ReactNode;
  }) => {
    // If assistant is logged in, check permissions
    // If admin is logged in (no assistant), always show
    if (assistant) {
      return (
        <WithPermission page={page} action={action}>
          {children}
        </WithPermission>
      );
    }
    return <>{children}</>;
  };
  const { data: ingredients } = useIngredients(selectedRestaurantId);
  const { data: inventory } = useInventory(selectedRestaurantId);
  const { data: categories } = useMenuCategories(selectedRestaurantId);
  const deleteMenuItemMutation = useDeleteMenuItem(selectedRestaurantId || '');
  const toggleAvailability = useToggleMenuItemAvailability(selectedRestaurantId || '');
  const createCategory = useCreateMenuCategory(selectedRestaurantId || '');
  const updateCategory = useUpdateMenuCategory(selectedRestaurantId || '');
  const deleteCategory = useDeleteMenuCategory(selectedRestaurantId || '');

  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState<any>(null);
  const [categoryFormData, setCategoryFormData] = useState({ nameAr: '', nameFr: '', quickNotes: [] as string[] });
  const [newQuickNote, setNewQuickNote] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [showOCRImport, setShowOCRImport] = useState(false);
  const [showIngredientsGraph, setShowIngredientsGraph] = useState(false);

  // Group menu items by category
  const groupedMenuItems = React.useMemo(() => {
    if (!menuItems) return {};
    return menuItems.reduce(
      (acc, item) => {
        const categoryId = item.categoryId || 'uncategorized';
        if (!acc[categoryId]) {
          acc[categoryId] = [];
        }
        acc[categoryId].push(item);
        return acc;
      },
      {} as Record<string, MenuItem[]>
    );
  }, [menuItems]);

  // Get category name helper
  const getCategoryName = React.useCallback(
    (categoryId: string | null) => {
      if (!categoryId) return t('menu.noCategory');
      const category = categories?.find((c) => c.id === categoryId);
      return category
        ? i18n.language === 'ar'
          ? category.nameAr
          : category.nameFr
        : t('menu.noCategory');
    },
    [categories, i18n.language, t]
  );

  // Toggle category expansion
  const toggleCategory = React.useCallback((categoryId: string) => {
    setExpandedCategories((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  }, []);

  // Fold all categories
  const foldAllCategories = React.useCallback(() => {
    setExpandedCategories(new Set());
  }, []);

  // Unfold all categories
  const unfoldAllCategories = React.useCallback(() => {
    setExpandedCategories(new Set(Object.keys(groupedMenuItems)));
  }, [groupedMenuItems]);

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingItem, setEditingItem] = useState<MenuItem | null>(null);
  const [deletingItemId, setDeletingItemId] = useState<string | null>(null);
  const [alertDialog, setAlertDialog] = useState<{
    open: boolean;
    title: string;
    description: string;
  }>({
    open: false,
    title: '',
    description: '',
  });
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    description: string;
    onConfirm: () => void;
  }>({
    open: false,
    title: '',
    description: '',
    onConfirm: () => {},
  });

  const handleToggleAvailability = async (menuItemId: string, currentStatus: boolean) => {
    try {
      await toggleAvailability.mutateAsync({ menuItemId, isAvailable: !currentStatus });
      await refetch();
    } catch (error) {
      console.error('Failed to toggle availability:', error);
      setAlertDialog({
        open: true,
        title: t('error.error'),
        description: t('error.generic'),
      });
    }
  };

  const handleDelete = async (menuItem: MenuItem) => {
    setConfirmDialog({
      open: true,
      title: t('menu.deleteMenuItem'),
      description: t('menu.deleteMenuItem') + '?',
      onConfirm: async () => {
        setDeletingItemId(menuItem.id);
        try {
          await deleteMenuItemMutation.mutateAsync({
            id: menuItem.id,
            imagePath: menuItem.imagePath,
          });
          await refetch();
        } catch (error) {
          console.error('Failed to delete menu item:', error);
          setAlertDialog({
            open: true,
            title: t('error.error'),
            description: t('error.generic'),
          });
        } finally {
          setDeletingItemId(null);
        }
      },
    });
  };

  if (!selectedRestaurantId) {
    return (
      <ProtectedRoute requiredRole="admin" requiredPermission="menu">
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
          <Header />
          <main className="container mx-auto px-4 py-8">
            <div className="p-4 text-center text-muted-foreground">
              {t('restaurant.selectRestaurant')}
            </div>
          </main>
        </div>
      </ProtectedRoute>
    );
  }

  if (isLoading) {
    return (
      <ProtectedRoute requiredRole="admin" requiredPermission="menu">
        <LoadingSpinner fullScreen />
      </ProtectedRoute>
    );
  }

  return (
    <ProtectedRoute requiredRole="admin" requiredPermission="menu">
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2">{t('menu.title')}</h1>
            <p className="text-muted-foreground">{t('menu.menuManagement')}</p>
          </div>

          <div className="space-y-6">
            {/* Categories Section */}
            <div>
              <div className="mb-4">
                <h2 className="text-xl font-semibold mb-4">{t('menu.categories')}</h2>

                {/* Import from Photo Button - Separate Row */}
                <ConditionalPermission page="menu" action="add">
                  <div className="space-y-2 mb-4">
                    <Button
                      variant="outline"
                      onClick={() => setShowOCRImport(true)}
                      className="w-full sm:w-auto"
                    >
                      <Camera className="w-4 h-4 me-2" />
                      <span className="truncate">{t('menu.ocrImport')}</span>
                    </Button>
                    <p className="text-sm text-muted-foreground">{t('menu.ocrImportDescription')}</p>
                  </div>
                </ConditionalPermission>

                {/* Add Category Button - Separate Row */}
                <ConditionalPermission page="menu" action="add">
                  <div>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setEditingCategory(null);
                        setCategoryFormData({ nameAr: '', nameFr: '', quickNotes: [] });
                        setNewQuickNote('');
                        setShowCategoryDialog(true);
                      }}
                      className="w-full sm:w-auto"
                    >
                      <Plus className="w-4 h-4 me-2" />
                      <span className="truncate">{t('menu.createCategory')}</span>
                    </Button>
                  </div>
                </ConditionalPermission>
              </div>
              {categories && categories.length > 0 ? (
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3 mb-6">
                  {categories.map((category, index) => (
                    <Card key={category.id}>
                      <CardContent className="p-4 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <ConditionalPermission page="menu" action="edit">
                            <div className="flex flex-col gap-0.5">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6"
                                disabled={index === 0 || updateCategory.isPending}
                                onClick={async () => {
                                  // Move up: swap positions with previous category
                                  if (index === 0) return;
                                  const prevCategory = categories[index - 1];
                                  if (!prevCategory) return;
                                  try {
                                    // Use index-based ordering to ensure unique values
                                    await updateCategory.mutateAsync({
                                      id: category.id,
                                      displayOrder: index - 1,
                                    });
                                    await updateCategory.mutateAsync({
                                      id: prevCategory.id,
                                      displayOrder: index,
                                    });
                                  } catch (error) {
                                    console.error('Failed to reorder categories:', error);
                                  }
                                }}
                              >
                                <ArrowUp className="w-3 h-3" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6"
                                disabled={index === categories.length - 1 || updateCategory.isPending}
                                onClick={async () => {
                                  // Move down: swap positions with next category
                                  if (index === categories.length - 1) return;
                                  const nextCategory = categories[index + 1];
                                  if (!nextCategory) return;
                                  try {
                                    // Use index-based ordering to ensure unique values
                                    await updateCategory.mutateAsync({
                                      id: category.id,
                                      displayOrder: index + 1,
                                    });
                                    await updateCategory.mutateAsync({
                                      id: nextCategory.id,
                                      displayOrder: index,
                                    });
                                  } catch (error) {
                                    console.error('Failed to reorder categories:', error);
                                  }
                                }}
                              >
                                <ArrowDown className="w-3 h-3" />
                              </Button>
                            </div>
                          </ConditionalPermission>
                          <div>
                            <p className="font-medium">
                              {i18n.language === 'ar' ? category.nameAr : category.nameFr}
                            </p>
                            {category.quickNotes && category.quickNotes.length > 0 && (
                              <p className="text-xs text-muted-foreground">
                                {category.quickNotes.length} {t('menu.quickNotes')}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <ConditionalPermission page="menu" action="edit">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                setEditingCategory(category);
                                setCategoryFormData({
                                  nameAr: category.nameAr,
                                  nameFr: category.nameFr,
                                  quickNotes: category.quickNotes || [],
                                });
                                setNewQuickNote('');
                                setShowCategoryDialog(true);
                              }}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                          </ConditionalPermission>
                          <ConditionalPermission page="menu" action="delete">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                setConfirmDialog({
                                  open: true,
                                  title: t('menu.deleteCategory'),
                                  description: t('menu.deleteCategoryConfirm'),
                                  onConfirm: async () => {
                                    try {
                                      await deleteCategory.mutateAsync(category.id);
                                    } catch (error) {
                                      console.error('Failed to delete category:', error);
                                    }
                                  },
                                });
                              }}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </ConditionalPermission>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <Card className="mb-6">
                  <CardContent className="py-4">
                    <p className="text-center text-muted-foreground text-sm">
                      {t('menu.noCategories')}
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Menu Items Section */}
            <div>
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0 mb-4">
                <div className="flex items-center gap-2">
                  <h2 className="text-xl font-semibold">{t('menu.title')}</h2>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    className="h-7 w-7"
                    onClick={unfoldAllCategories}
                    title={t('menu.expandAll')}
                  >
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    className="h-7 w-7"
                    onClick={foldAllCategories}
                    title={t('menu.collapseAll')}
                  >
                    <ChevronUp className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2 w-full sm:w-auto">
                  <Button variant="outline" onClick={() => setShowIngredientsGraph(true)} className="w-full sm:w-auto">
                    <BarChart3 className="w-4 h-4 me-2" />
                    <span className="truncate">{t('menu.seeGraph')}</span>
                  </Button>
                  <ConditionalPermission page="menu" action="add">
                    <Button onClick={() => setShowCreateDialog(true)} className="w-full sm:w-auto">
                      <Plus className="w-4 h-4 me-2" />
                      <span className="truncate">{t('menu.createMenuItem')}</span>
                    </Button>
                  </ConditionalPermission>
                </div>
              </div>

              {!menuItems || menuItems.length === 0 ? (
                <Card>
                  <CardContent className="py-8">
                    <p className="text-center text-muted-foreground">{t('menu.noMenuItems')}</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-6">
                  {Object.entries(groupedMenuItems).map(([categoryId, items]) => {
                    const isExpanded = expandedCategories.has(categoryId);
                    return (
                      <div key={categoryId} className="space-y-3">
                        <button
                          type="button"
                          onClick={() => toggleCategory(categoryId)}
                          className="w-full flex items-center gap-2 font-semibold text-lg text-foreground hover:text-primary transition-colors"
                        >
                          {isExpanded ? (
                            <ChevronDown className="w-5 h-5" />
                          ) : (
                            <ChevronRight className="w-5 h-5" />
                          )}
                          <span>
                            {getCategoryName(categoryId === 'uncategorized' ? null : categoryId)}
                          </span>
                          <span className="text-sm font-normal text-muted-foreground">
                            ({items.length})
                          </span>
                        </button>
                        {isExpanded && (
                          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 ms-0 sm:ms-7">
                            {items.map((item) => (
                              <Card key={item.id} className="flex flex-col h-full">
                                <CardHeader className="p-3 sm:p-6 pb-2 sm:pb-3 flex-shrink-0">
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="flex-1 min-w-0 pr-2">
                                      <CardTitle className="text-base sm:text-lg font-semibold leading-tight tracking-tight block w-full break-words m-0">
                                        {i18n.language === 'ar' ? item.nameAr : item.nameFr}
                                      </CardTitle>
                                      <CardDescription className="text-sm block w-full m-0 mt-1">
                                        {item.price.toFixed(2)} DH
                                      </CardDescription>
                                    </div>
                                    <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                                      {item.isAvailable ? (
                                        <CheckCircle className="w-4 h-4 text-green-600" />
                                      ) : (
                                        <XCircle className="w-4 h-4 text-red-600" />
                                      )}
                                      <ConditionalPermission page="menu" action="edit">
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          className="h-8 w-8 sm:h-10 sm:w-10"
                                          onClick={() => setEditingItem(item)}
                                        >
                                          <Pencil className="w-3 h-3 sm:w-4 sm:h-4" />
                                        </Button>
                                      </ConditionalPermission>
                                    </div>
                                  </div>
                                </CardHeader>
                                <CardContent className="p-3 sm:p-6 pt-0 sm:pt-2 flex flex-col flex-1">
                                  {item.imageUrl && (
                                    <div className="mb-2 sm:mb-3 rounded-lg overflow-hidden">
                                      <img
                                        src={item.imageUrl}
                                        alt={i18n.language === 'ar' ? item.nameAr : item.nameFr}
                                        className="w-full h-32 sm:h-48 object-cover"
                                      />
                                    </div>
                                  )}

                                  {(i18n.language === 'ar'
                                    ? item.descriptionAr
                                    : item.descriptionFr) && (
                                    <p className="text-xs sm:text-sm text-muted-foreground mb-2 sm:mb-3 line-clamp-2">
                                      {i18n.language === 'ar'
                                        ? item.descriptionAr
                                        : item.descriptionFr}
                                    </p>
                                  )}

                                  <MenuItemSpecialtiesBadges specialties={allSpecialtiesMap?.get(item.id) || []} />

                                  <div className="flex flex-row gap-2 mt-auto pt-2 sm:pt-4">
                                    <ConditionalPermission page="menu" action="edit">
                                      <Button
                                        variant={item.isAvailable ? 'outline' : 'default'}
                                        size="sm"
                                        className="flex-1 h-8 sm:h-9 text-xs sm:text-sm whitespace-nowrap"
                                        onClick={() =>
                                          handleToggleAvailability(item.id, item.isAvailable)
                                        }
                                      >
                                        {item.isAvailable
                                          ? t('menu.unavailable')
                                          : t('menu.available')}
                                      </Button>
                                    </ConditionalPermission>
                                    <ConditionalPermission page="menu" action="delete">
                                      <Button
                                        variant="destructive"
                                        size="sm"
                                        className="flex-1 h-8 sm:h-9 text-xs sm:text-sm"
                                        onClick={() => handleDelete(item)}
                                        disabled={deletingItemId === item.id}
                                      >
                                        <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                                      </Button>
                                    </ConditionalPermission>
                                  </div>
                                </CardContent>
                              </Card>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </main>

        {showCreateDialog && selectedRestaurantId && (
          <MenuItemDialog
            restaurantId={selectedRestaurantId}
            ingredients={ingredients || []}
            inventory={inventory || []}
            categories={categories || []}
            open={showCreateDialog}
            onOpenChange={setShowCreateDialog}
            onSuccess={() => refetch()}
          />
        )}

        {editingItem && selectedRestaurantId && (
          <MenuItemDialog
            restaurantId={selectedRestaurantId}
            menuItem={editingItem}
            ingredients={ingredients || []}
            inventory={inventory || []}
            categories={categories || []}
            open={!!editingItem}
            onOpenChange={(open) => !open && setEditingItem(null)}
            onSuccess={() => refetch()}
          />
        )}

        {/* Category Dialog */}
        <Dialog open={showCategoryDialog} onOpenChange={setShowCategoryDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {editingCategory ? t('menu.editCategory') : t('menu.createCategory')}
              </DialogTitle>
              <DialogDescription>{t('menu.categoryDescription')}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 max-h-[60vh] overflow-y-auto">
              <div>
                <Label htmlFor="categoryNameAr">{t('menu.nameAr')}</Label>
                <Input
                  id="categoryNameAr"
                  value={categoryFormData.nameAr}
                  onChange={(e) =>
                    setCategoryFormData({ ...categoryFormData, nameAr: e.target.value })
                  }
                />
              </div>
              <div>
                <Label htmlFor="categoryNameFr">{t('menu.nameFr')}</Label>
                <Input
                  id="categoryNameFr"
                  value={categoryFormData.nameFr}
                  onChange={(e) =>
                    setCategoryFormData({ ...categoryFormData, nameFr: e.target.value })
                  }
                />
              </div>
              
              {/* Quick Notes Section */}
              <div className="border-t pt-4">
                <Label className="text-base font-semibold">{t('menu.quickNotes')}</Label>
                <p className="text-sm text-muted-foreground mb-3">{t('menu.quickNotesDescription')}</p>
                
                {/* Add Quick Note */}
                <div className="flex gap-2 mb-3">
                  <Input
                    placeholder={t('menu.addQuickNotePlaceholder')}
                    value={newQuickNote}
                    onChange={(e) => setNewQuickNote(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && newQuickNote.trim()) {
                        e.preventDefault();
                        setCategoryFormData({
                          ...categoryFormData,
                          quickNotes: [...categoryFormData.quickNotes, newQuickNote.trim()],
                        });
                        setNewQuickNote('');
                      }
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      if (newQuickNote.trim()) {
                        setCategoryFormData({
                          ...categoryFormData,
                          quickNotes: [...categoryFormData.quickNotes, newQuickNote.trim()],
                        });
                        setNewQuickNote('');
                      }
                    }}
                    disabled={!newQuickNote.trim()}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                
                {/* Quick Notes List */}
                {categoryFormData.quickNotes.length > 0 && (
                  <div className="space-y-2">
                    {categoryFormData.quickNotes.map((note, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-muted rounded-md">
                        <span className="text-sm">{note}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => {
                            setCategoryFormData({
                              ...categoryFormData,
                              quickNotes: categoryFormData.quickNotes.filter((_, i) => i !== index),
                            });
                          }}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCategoryDialog(false)}>
                {t('common.cancel')}
              </Button>
              <Button
                onClick={async () => {
                  try {
                    if (editingCategory) {
                      await updateCategory.mutateAsync({
                        id: editingCategory.id,
                        nameAr: categoryFormData.nameAr,
                        nameFr: categoryFormData.nameFr,
                        quickNotes: categoryFormData.quickNotes,
                      });
                    } else {
                      await createCategory.mutateAsync({
                        nameAr: categoryFormData.nameAr,
                        nameFr: categoryFormData.nameFr,
                        quickNotes: categoryFormData.quickNotes,
                      });
                    }
                    setShowCategoryDialog(false);
                    setEditingCategory(null);
                    setCategoryFormData({ nameAr: '', nameFr: '', quickNotes: [] });
                    setNewQuickNote('');
                  } catch (error) {
                    console.error('Failed to save category:', error);
                  }
                }}
              >
                {t('common.save')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <AlertModal
          open={alertDialog.open}
          onOpenChange={(open) => setAlertDialog({ ...alertDialog, open })}
          title={alertDialog.title}
          description={alertDialog.description}
        />

        <ConfirmModal
          open={confirmDialog.open}
          onOpenChange={(open) => setConfirmDialog({ ...confirmDialog, open })}
          onConfirm={confirmDialog.onConfirm}
          title={confirmDialog.title}
          description={confirmDialog.description}
          variant="destructive"
        />

        {/* OCR Import Dialog */}
        {selectedRestaurantId && (
          <MenuOCRImport
            restaurantId={selectedRestaurantId}
            open={showOCRImport}
            onOpenChange={setShowOCRImport}
          />
        )}

        {/* Ingredients Graph Dialog */}
        {selectedRestaurantId && (
          <IngredientsGraphDialog
            restaurantId={selectedRestaurantId}
            open={showIngredientsGraph}
            onOpenChange={setShowIngredientsGraph}
          />
        )}
      </div>
    </ProtectedRoute>
  );
}

// Menu Item Dialog Component
function MenuItemDialog({
  restaurantId,
  menuItem,
  ingredients,
  inventory,
  categories,
  open,
  onOpenChange,
  onSuccess,
}: {
  restaurantId: string;
  menuItem?: MenuItem;
  ingredients: Ingredient[];
  inventory: any[];
  categories: any[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { data: assistant } = useCurrentAssistant();
  const queryClient = useQueryClient();

  // Helper to conditionally wrap with permission check
  const ConditionalPermission = ({
    page,
    action,
    children,
  }: {
    page: keyof AssistantPermissions;
    action: string;
    children: React.ReactNode;
  }) => {
    // If assistant is logged in, check permissions
    // If admin is logged in (no assistant), always show
    if (assistant) {
      return (
        <WithPermission page={page} action={action}>
          {children}
        </WithPermission>
      );
    }
    return <>{children}</>;
  };
  const [formData, setFormData] = useState({
    nameAr: menuItem?.nameAr || '',
    nameFr: menuItem?.nameFr || '',
    descriptionAr: menuItem?.descriptionAr || '',
    descriptionFr: menuItem?.descriptionFr || '',
    price: menuItem?.price || 0,
    imageUrl: menuItem?.imageUrl || null,
    imagePath: menuItem?.imagePath || null,
    isAvailable: menuItem?.isAvailable ?? true,
    categoryId: menuItem?.categoryId || null,
    optionsAreRequired: menuItem?.optionsAreRequired ?? false,
    ingredients: [] as MenuItemIngredientInput[],
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [selectedIngredientId, setSelectedIngredientId] = useState<string>('');
  const [ingredientQuantity, setIngredientQuantity] = useState<string>('');
  const [ingredientUnit, setIngredientUnit] = useState<MeasurementUnit>('g');
  const [ingredientOptionId, setIngredientOptionId] = useState<string | null>(null);
  const [availableUnits, setAvailableUnits] = useState<MeasurementUnit[]>([]);

  // Options state
  const [showOptionsSection, setShowOptionsSection] = useState(false);
  const [newOptionNameAr, setNewOptionNameAr] = useState('');
  const [newOptionNameFr, setNewOptionNameFr] = useState('');
  const [newOptionPriceModifier, setNewOptionPriceModifier] = useState(0);
  const [newOptionIsDefault, setNewOptionIsDefault] = useState(false);
  const [editingOption, setEditingOption] = useState<MenuItemOption | null>(null);
  // Temporary options for new menu items (before they are saved)
  const [temporaryOptions, setTemporaryOptions] = useState<Array<{
    id: string;
    nameAr: string;
    nameFr: string;
    priceModifier: number;
    isDefault: boolean;
    sortOrder: number;
  }>>([]);

  const createMutation = useCreateMenuItem();
  const updateMutation = useUpdateMenuItem(menuItem?.id || '', restaurantId);

  // Specialties hooks
  const { data: specialties } = useKitchenSpecialties(restaurantId);
  const { data: menuItemSpecialties } = useMenuItemSpecialties(menuItem?.id || null);
  const setSpecialtiesMutation = useSetMenuItemSpecialties();
  const [selectedSpecialtyIds, setSelectedSpecialtyIds] = useState<string[]>([]);

  // Options hooks (only for existing menu items)
  const { data: options, refetch: refetchOptions } = useMenuItemOptions(menuItem?.id || null);
  const createOptionMutation = useCreateMenuItemOption();
  const updateOptionMutation = useUpdateMenuItemOption(menuItem?.id || '');
  const deleteOptionMutation = useDeleteMenuItemOption(menuItem?.id || '');

  // Combined options list: use real options for existing menu items, temporary options for new ones
  const displayOptions = menuItem ? options : temporaryOptions;

  // Load existing ingredients when editing
  const { data: menuItemWithIngredients } = useMenuItemWithIngredients(menuItem?.id || '');

  // Load existing ingredients and optionsAreRequired when editing
  useEffect(() => {
    if (menuItemWithIngredients) {
      setFormData((prev) => ({
        ...prev,
        optionsAreRequired: menuItemWithIngredients.optionsAreRequired ?? false,
        ingredients: menuItemWithIngredients.ingredients.map((ing) => ({
          ingredientId: ing.ingredientId,
          quantity: ing.quantity,
          unit: ing.unit,
          menuItemOptionId: ing.menuItemOptionId || null,
        })),
      }));
    }
  }, [menuItemWithIngredients]);

  // Initialize component state when dialog opens
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (open && !menuItem && !isInitialized) {
      // Opening create dialog for the first time - start with empty temporary options
      setTemporaryOptions([]);
      setIsInitialized(true);
    } else if (!open) {
      // Dialog closed - reset initialization flag
      setIsInitialized(false);
    }
  }, [open, menuItem, isInitialized]);

  // Show options section if there are options
  useEffect(() => {
    if (options && options.length > 0) {
      setShowOptionsSection(true);
    }
  }, [options]);

  // Refetch options when editing an existing menu item (to ensure fresh data)
  useEffect(() => {
    if (menuItem?.id && open) {
      // Invalidate the query cache to force a refetch
      queryClient.invalidateQueries({ queryKey: ['menu-item-options', menuItem.id] });
      refetchOptions();
    }
  }, [menuItem?.id, open, refetchOptions, queryClient]);

  // Load existing specialties when editing
  useEffect(() => {
    if (menuItemSpecialties) {
      setSelectedSpecialtyIds(menuItemSpecialties.map((s: any) => s.specialtyId));
    }
  }, [menuItemSpecialties]);

  // Handle adding an option
  const handleAddOption = async () => {
    // Require at least one language field (trim whitespace)
    const nameAr = (newOptionNameAr || '').trim();
    const nameFr = (newOptionNameFr || '').trim();
    if (nameAr.length === 0 && nameFr.length === 0) return;

    // Apply language fallback
    const optionData = applyLanguageFallback({
      nameAr,
      nameFr,
    });

    if (menuItem?.id) {
      // For existing menu items, save to database
      try {
        await createOptionMutation.mutateAsync({
          menuItemId: menuItem.id,
          nameAr: optionData.nameAr || '',
          nameFr: optionData.nameFr || '',
          priceModifier: newOptionPriceModifier,
          isDefault: newOptionIsDefault,
          sortOrder: options?.length || 0,
        });
        setNewOptionNameAr('');
        setNewOptionNameFr('');
        setNewOptionPriceModifier(0);
        setNewOptionIsDefault(false);
        refetchOptions();
      } catch (error) {
        console.error('Failed to create option:', error);
      }
    } else {
      // For new menu items, add to temporary options
      setTemporaryOptions([
        ...temporaryOptions,
        {
          id: `temp-${Date.now()}`, // Temporary ID
          nameAr: optionData.nameAr || '',
          nameFr: optionData.nameFr || '',
          priceModifier: newOptionPriceModifier,
          isDefault: newOptionIsDefault,
          sortOrder: temporaryOptions.length,
        },
      ]);
      setNewOptionNameAr('');
      setNewOptionNameFr('');
      setNewOptionPriceModifier(0);
      setNewOptionIsDefault(false);
    }
  };

  // Handle updating an option
  const handleUpdateOption = async () => {
    if (!editingOption) return;

    // Require at least one language field (trim whitespace)
    const nameAr = (editingOption.nameAr || '').trim();
    const nameFr = (editingOption.nameFr || '').trim();
    if (nameAr.length === 0 && nameFr.length === 0) return;

    // Apply language fallback
    const optionData = applyLanguageFallback({
      nameAr,
      nameFr,
    });

    if (menuItem?.id) {
      // For existing menu items, update in database
      try {
        await updateOptionMutation.mutateAsync({
          optionId: editingOption.id,
          nameAr: optionData.nameAr || '',
          nameFr: optionData.nameFr || '',
          priceModifier: editingOption.priceModifier,
          isDefault: editingOption.isDefault,
          sortOrder: editingOption.sortOrder,
        });
        setEditingOption(null);
        refetchOptions();
      } catch (error) {
        console.error('Failed to update option:', error);
      }
    } else {
      // For new menu items, update temporary options
      setTemporaryOptions(
        temporaryOptions.map((opt) =>
          opt.id === editingOption.id
            ? {
                ...opt,
                nameAr: optionData.nameAr || '',
                nameFr: optionData.nameFr || '',
                priceModifier: editingOption.priceModifier,
                isDefault: editingOption.isDefault,
              }
            : opt
        )
      );
      setEditingOption(null);
    }
  };

  // Handle deleting an option
  const handleDeleteOption = async (optionId: string) => {
    if (menuItem?.id) {
      // For existing menu items, delete from database
      try {
        await deleteOptionMutation.mutateAsync(optionId);
        refetchOptions();
      } catch (error) {
        console.error('Failed to delete option:', error);
      }
    } else {
      // For new menu items, remove from temporary options
      setTemporaryOptions(temporaryOptions.filter((opt) => opt.id !== optionId));
    }
  };

  // Get option name helper
  const getOptionName = (optionId: string | null) => {
    if (!optionId) return t('menu.allOptions');
    const option = displayOptions?.find((o) => o.id === optionId);
    return option ? (i18n.language === 'ar' ? option.nameAr : option.nameFr) : t('menu.allOptions');
  };

  // Update available units when an ingredient is selected
  useEffect(() => {
    if (selectedIngredientId) {
      const ingredientInventory = inventory.find(
        (inv) => inv.ingredientId === selectedIngredientId
      );

      if (ingredientInventory) {
        const inventoryUnit = ingredientInventory.unit;
        const compatibleUnits = getCompatibleUnits(inventoryUnit);

        setAvailableUnits(compatibleUnits);
        setIngredientUnit(inventoryUnit); // Set default to inventory unit
      } else {
        // If no inventory found, allow all units
        setAvailableUnits(['g', 'kg', 'ml', 'cl', 'l', 'unit']);
      }
    }
  }, [selectedIngredientId, inventory]);

  const handleAddIngredient = () => {
    const quantity = parseFloat(ingredientQuantity);
    if (!selectedIngredientId || !ingredientQuantity || quantity <= 0 || isNaN(quantity)) {
      return;
    }

    // Check if already added with same option
    const alreadyAdded = formData['ingredients'].some(
      (ing) =>
        ing.ingredientId === selectedIngredientId && ing.menuItemOptionId === ingredientOptionId
    );
    if (alreadyAdded) {
      setErrors({ ...errors, ingredient: t('menu.ingredientAlreadyAdded') });
      return;
    }

    setFormData({
      ...formData,
      ingredients: [
        ...formData['ingredients'],
        {
          ingredientId: selectedIngredientId,
          quantity: quantity,
          unit: ingredientUnit,
          menuItemOptionId: ingredientOptionId,
        },
      ],
    });

    // Reset ingredient form
    setSelectedIngredientId('');
    setIngredientQuantity('');
    setIngredientUnit('g');
    setIngredientOptionId(null);
    setErrors({ ...errors, ingredient: '' });
  };

  const handleRemoveIngredient = (ingredientId: string, optionId: string | null | undefined) => {
    setFormData({
      ...formData,
      ingredients: formData['ingredients'].filter(
        (ing) => !(ing.ingredientId === ingredientId && ing.menuItemOptionId === optionId)
      ),
    });
  };

  const getIngredientName = (ingredientId: string) => {
    const ingredient = ingredients.find((ing) => ing.id === ingredientId);
    if (!ingredient) return '';
    return i18n.language === 'ar' ? ingredient.nameAr : ingredient.nameFr;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    const newErrors: Record<string, string> = {};
    // Require at least one language field (trim whitespace to match schema validation)
    const nameAr = (formData['nameAr'] || '').trim();
    const nameFr = (formData['nameFr'] || '').trim();
    if (nameAr.length === 0 && nameFr.length === 0) {
      newErrors['nameAr'] = t('validation.required');
      newErrors['nameFr'] = t('validation.required');
    }
    if (formData['price'] <= 0) newErrors['price'] = t('validation.positive');

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Capture temporaryOptions in a local variable to prevent race conditions with state updates
    const optionsToCreate = [...temporaryOptions];

    try {
      let menuItemId = menuItem?.id;

      if (menuItem) {
        await updateMutation.mutateAsync({
          ...formData,
          oldImagePath: menuItem.imagePath,
        });
      } else {
        // The mutation returns the ID directly, not an object with an id property
        menuItemId = await createMutation.mutateAsync({ ...formData, restaurantId });

        // Create temporary options for new menu items
        if (menuItemId && optionsToCreate.length > 0) {
          // Create all options in parallel and wait for all to complete
          await Promise.all(
            optionsToCreate.map((option) =>
              createOptionMutation.mutateAsync({
                menuItemId: menuItemId as string,
                nameAr: option.nameAr,
                nameFr: option.nameFr,
                priceModifier: option.priceModifier,
                isDefault: option.isDefault,
                sortOrder: option.sortOrder,
              })
            )
          );
          // Manually invalidate the query to ensure it's refetched next time
          queryClient.invalidateQueries({ queryKey: ['menu-item-options', menuItemId] });
        }
      }

      // Save specialties if we have a menu item ID
      if (menuItemId && selectedSpecialtyIds.length > 0) {
        await setSpecialtiesMutation.mutateAsync({
          menuItemId,
          specialtyIds: selectedSpecialtyIds,
        });
      } else if (menuItemId) {
        // Clear specialties if none selected
        await setSpecialtiesMutation.mutateAsync({
          menuItemId,
          specialtyIds: [],
        });
      }

      // Clear temporary options after successful creation
      setTemporaryOptions([]);

      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      console.error('Error saving menu item:', error);
      setErrors({ form: error instanceof Error ? error.message : t('error.generic') });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full sm:min-w-[60vw] max-h-[95vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>
              {menuItem ? t('menu.editMenuItem') : t('menu.createMenuItem')}
            </DialogTitle>
            <DialogDescription>{t('menu.menuManagement')}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="nameAr">
                {t('menu.name')} ({t('user.language')} AR)
                {!(formData['nameFr'] || '').trim() && <span className="text-destructive"> *</span>}
              </Label>
              <Input
                id="nameAr"
                value={formData['nameAr']}
                onChange={(e) => setFormData({ ...formData, nameAr: e.target.value })}
                disabled={createMutation.isPending || updateMutation.isPending}
              />
              {errors['nameAr'] && <p className="text-sm text-destructive">{errors['nameAr']}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="nameFr">
                {t('menu.name')} ({t('user.language')} FR)
                {!(formData['nameAr'] || '').trim() && <span className="text-destructive"> *</span>}
              </Label>
              <Input
                id="nameFr"
                value={formData['nameFr']}
                onChange={(e) => setFormData({ ...formData, nameFr: e.target.value })}
                disabled={createMutation.isPending || updateMutation.isPending}
              />
              {errors['nameFr'] && <p className="text-sm text-destructive">{errors['nameFr']}</p>}
            </div>
            <p className="text-sm text-muted-foreground">
              {t('settings.oneLanguageRequired') || 'At least one language is required'}
            </p>

            <div className="space-y-2">
              <Label htmlFor="category">{t('menu.category')}</Label>
              <Select
                value={formData.categoryId || 'none'}
                onValueChange={(value) =>
                  setFormData({ ...formData, categoryId: value === 'none' ? null : value })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('menu.selectCategory')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t('menu.noCategory')}</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {i18n.language === 'ar' ? cat.nameAr : cat.nameFr}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Kitchen Specialties Selection */}
            {specialties && specialties.length > 0 && (
              <div className="space-y-2">
                <Label>{t('settings.selectSpecialties')}</Label>
                <div className="flex flex-wrap gap-2">
                  {specialties
                    .filter((s) => s.isActive)
                    .map((specialty) => {
                      const isSelected = selectedSpecialtyIds.includes(specialty.id);
                      return (
                        <button
                          key={specialty.id}
                          type="button"
                          onClick={() => {
                            if (isSelected) {
                              setSelectedSpecialtyIds(
                                selectedSpecialtyIds.filter((id) => id !== specialty.id)
                              );
                            } else {
                              setSelectedSpecialtyIds([...selectedSpecialtyIds, specialty.id]);
                            }
                          }}
                          className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                            isSelected ? 'text-white' : 'bg-muted hover:bg-muted/80 text-foreground'
                          }`}
                          style={isSelected ? { backgroundColor: specialty.color } : {}}
                        >
                          {i18n.language === 'ar' ? specialty.nameAr : specialty.nameFr}
                        </button>
                      );
                    })}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="price">{t('menu.price')}</Label>
              <Input
                id="price"
                type="number"
                step="0.01"
                min="0"
                value={formData['price'] === 0 ? '' : formData['price']}
                onChange={(e) => {
                  const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
                  setFormData({ ...formData, price: isNaN(value) ? 0 : value });
                }}
                placeholder="0.00"
                disabled={createMutation.isPending || updateMutation.isPending}
              />
              {errors['price'] && <p className="text-sm text-destructive">{errors['price']}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="descriptionAr">{t('menu.description')} (AR)</Label>
              <Input
                id="descriptionAr"
                value={formData['descriptionAr']}
                onChange={(e) => setFormData({ ...formData, descriptionAr: e.target.value })}
                disabled={createMutation.isPending || updateMutation.isPending}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="descriptionFr">{t('menu.description')} (FR)</Label>
              <Input
                id="descriptionFr"
                value={formData['descriptionFr']}
                onChange={(e) => setFormData({ ...formData, descriptionFr: e.target.value })}
                disabled={createMutation.isPending || updateMutation.isPending}
              />
            </div>

            {/* Image Upload Section */}
            <div className="space-y-2">
              <Label>{t('menu.image') || 'Image'}</Label>
              <ImageUpload
                currentImageUrl={formData.imageUrl}
                onImageUpload={(url, path) => {
                  setFormData({ ...formData, imageUrl: url, imagePath: path });
                }}
                onImageRemove={() => {
                  setFormData({ ...formData, imageUrl: null, imagePath: null });
                }}
                disabled={createMutation.isPending || updateMutation.isPending}
              />
            </div>

            {/* Options Section */}
            <div className="space-y-4 pt-4 border-t">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold flex items-center gap-2">
                  <Settings className="w-4 h-4" />
                  {t('menu.options')}
                </Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowOptionsSection(!showOptionsSection)}
                >
                  {showOptionsSection ? t('common.hide') : t('common.show')}
                </Button>
              </div>

              {showOptionsSection && (
                <div className="space-y-4">
                  {/* Options Required Toggle */}
                  {(displayOptions && displayOptions.length > 0) && (
                    <div className="flex items-center justify-between p-3 bg-muted/50 rounded-md">
                      <div className="space-y-0.5">
                        <Label htmlFor="optionsAreRequired" className="text-sm font-medium">
                          {t('menu.optionsAreRequired')}
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          {t('menu.optionsAreRequiredDescription')}
                        </p>
                      </div>
                      <Switch
                        id="optionsAreRequired"
                        checked={formData.optionsAreRequired}
                        onCheckedChange={(checked) =>
                          setFormData({ ...formData, optionsAreRequired: checked })
                        }
                      />
                    </div>
                  )}

                  {/* Existing Options List */}
                  {displayOptions && displayOptions.length > 0 && (
                      <div className="space-y-2">
                        {displayOptions.map((option) => (
                          <div
                            key={option.id}
                            className="flex items-center justify-between p-3 bg-muted rounded-md"
                          >
                            {editingOption?.id === option.id ? (
                              <div className="flex-1 space-y-3">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                  <div className="space-y-2">
                                    <Label htmlFor="editOptionNameAr">
                                      {t('menu.nameAr')}
                                      {!editingOption.nameFr?.trim() && (
                                        <span className="text-destructive"> *</span>
                                      )}
                                    </Label>
                                    <Input
                                      id="editOptionNameAr"
                                      value={editingOption.nameAr}
                                      onChange={(e) =>
                                        setEditingOption({ ...editingOption, nameAr: e.target.value })
                                      }
                                      placeholder={t('menu.nameAr')}
                                    />
                                  </div>
                                  <div className="space-y-2">
                                    <Label htmlFor="editOptionNameFr">
                                      {t('menu.nameFr')}
                                      {!editingOption.nameAr?.trim() && (
                                        <span className="text-destructive"> *</span>
                                      )}
                                    </Label>
                                    <Input
                                      id="editOptionNameFr"
                                      value={editingOption.nameFr}
                                      onChange={(e) =>
                                        setEditingOption({ ...editingOption, nameFr: e.target.value })
                                      }
                                      placeholder={t('menu.nameFr')}
                                    />
                                  </div>
                                </div>
                                <p className="text-sm text-muted-foreground">
                                  {t('settings.oneLanguageRequired') ||
                                    'At least one language is required'}
                                </p>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                  <div className="space-y-2">
                                    <Label htmlFor="editOptionPriceModifier">
                                      {t('menu.priceModifier')}
                                    </Label>
                                    <Input
                                      id="editOptionPriceModifier"
                                      type="number"
                                      value={editingOption.priceModifier === 0 ? '' : editingOption.priceModifier}
                                      onChange={(e) => {
                                        const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
                                        setEditingOption({
                                          ...editingOption,
                                          priceModifier: isNaN(value) ? 0 : value,
                                        });
                                      }}
                                      placeholder="0.00"
                                    />
                                  </div>
                                  <div className="space-y-2">
                                    <Label>{t('menu.isDefault')}</Label>
                                    <div className="flex items-center gap-2 h-10">
                                      <Switch
                                        checked={editingOption.isDefault}
                                        onCheckedChange={(checked) =>
                                          setEditingOption({ ...editingOption, isDefault: checked })
                                        }
                                      />
                                      <span className="text-sm">{t('menu.isDefault')}</span>
                                    </div>
                                  </div>
                                </div>
                                <div className="flex gap-2">
                                  <Button
                                    type="button"
                                    size="sm"
                                    onClick={handleUpdateOption}
                                    disabled={
                                      (!editingOption.nameAr?.trim() &&
                                        !editingOption.nameFr?.trim()) ||
                                      updateOptionMutation.isPending
                                    }
                                  >
                                    {t('common.save')}
                                  </Button>
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setEditingOption(null)}
                                  >
                                    {t('common.cancel')}
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <>
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium">
                                      {i18n.language === 'ar' ? option.nameAr : option.nameFr}
                                    </span>
                                    {option.isDefault && (
                                      <Badge variant="secondary">{t('menu.isDefault')}</Badge>
                                    )}
                                  </div>
                                  <span className="text-sm text-muted-foreground">
                                    {option.priceModifier >= 0 ? '+' : ''}
                                    {formatPrice(option.priceModifier)} DH
                                  </span>
                                </div>
                                <div className="flex gap-2">
                                  <ConditionalPermission page="menu" action="edit">
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => setEditingOption(option as MenuItemOption)}
                                    >
                                      <Pencil className="w-4 h-4" />
                                    </Button>
                                  </ConditionalPermission>
                                  <ConditionalPermission page="menu" action="delete">
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => handleDeleteOption(option.id)}
                                      disabled={deleteOptionMutation.isPending}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </ConditionalPermission>
                                </div>
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Add New Option Form */}
                    <div className="space-y-3">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="newOptionNameAr">
                            {t('menu.optionName')} ({t('menu.nameAr')})
                            {!newOptionNameFr.trim() && <span className="text-destructive"> *</span>}
                          </Label>
                          <Input
                            id="newOptionNameAr"
                            value={newOptionNameAr}
                            onChange={(e) => setNewOptionNameAr(e.target.value)}
                            placeholder={`${t('menu.optionName')} (${t('menu.nameAr')})`}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="newOptionNameFr">
                            {t('menu.optionName')} ({t('menu.nameFr')})
                            {!newOptionNameAr.trim() && <span className="text-destructive"> *</span>}
                          </Label>
                          <Input
                            id="newOptionNameFr"
                            value={newOptionNameFr}
                            onChange={(e) => setNewOptionNameFr(e.target.value)}
                            placeholder={`${t('menu.optionName')} (${t('menu.nameFr')})`}
                          />
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {t('settings.oneLanguageRequired') || 'At least one language is required'}
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="newOptionPriceModifier">{t('menu.priceModifier')}</Label>
                          <Input
                            id="newOptionPriceModifier"
                            type="number"
                            value={newOptionPriceModifier === 0 ? '' : newOptionPriceModifier}
                            onChange={(e) => {
                              const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
                              setNewOptionPriceModifier(isNaN(value) ? 0 : value);
                            }}
                            placeholder="0.00"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>{t('menu.isDefault')}</Label>
                          <div className="flex items-center gap-2 h-10">
                            <Switch
                              checked={newOptionIsDefault}
                              onCheckedChange={setNewOptionIsDefault}
                            />
                            <span className="text-sm">{t('menu.isDefault')}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <ConditionalPermission page="menu" action="add">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleAddOption}
                        disabled={
                          (!newOptionNameAr.trim() && !newOptionNameFr.trim()) ||
                          createOptionMutation.isPending
                        }
                      >
                        <Plus className="w-4 h-4 me-2" />
                        <span className="truncate">{t('menu.addOption')}</span>
                      </Button>
                    </ConditionalPermission>
                  </div>
                )}
            </div>

            {/* Ingredient Picker Section */}
            <div className="space-y-4 pt-4 border-t">
              <Label className="text-base font-semibold">{t('menu.ingredients')}</Label>

              {/* Ingredient Selection Form */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[2fr_1.5fr_1fr_1fr_auto] gap-4">
                <div className="space-y-2 sm:col-span-2 lg:col-span-1">
                  <Label className="text-sm font-medium">{t('menu.selectIngredient')}</Label>
                  <Select
                    value={selectedIngredientId}
                    onValueChange={setSelectedIngredientId}
                    disabled={createMutation.isPending || updateMutation.isPending}
                  >
                    <SelectTrigger className="h-11">
                      <SelectValue placeholder={t('menu.selectIngredient')} />
                    </SelectTrigger>
                    <SelectContent>
                      {ingredients.map((ingredient) => (
                        <SelectItem key={ingredient.id} value={ingredient.id}>
                          <div className="flex items-center gap-2">
                            <IngredientIcon ingredient={ingredient} size={20} />
                            <span>
                              {i18n.language === 'ar' ? ingredient.nameAr : ingredient.nameFr}
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2 sm:col-span-1">
                  <Label className="text-sm font-medium">{t('inventory.quantity')}</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder={t('inventory.quantity')}
                    value={ingredientQuantity}
                    onChange={(e) => setIngredientQuantity(e.target.value)}
                    disabled={createMutation.isPending || updateMutation.isPending}
                    className="h-11 min-w-[120px] w-full"
                  />
                </div>

                <div className="space-y-2 sm:col-span-1 lg:col-span-1">
                  <Label className="text-sm font-medium">{t('inventory.unit')}</Label>
                  <Select
                    value={ingredientUnit}
                    onValueChange={(value) => setIngredientUnit(value as MeasurementUnit)}
                    disabled={
                      createMutation.isPending ||
                      updateMutation.isPending ||
                      availableUnits.length === 0
                    }
                  >
                    <SelectTrigger className="h-11 min-w-[80px] w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {availableUnits.map((unit) => (
                        <SelectItem key={unit} value={unit}>
                          {t(`inventory.units.${unit}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Option selector - only show if options exist */}
                <div className="space-y-2 sm:col-span-1">
                  <Label className="text-sm font-medium">{t('menu.forOption')}</Label>
                  <Select
                    value={ingredientOptionId || 'all'}
                    onValueChange={(value) => setIngredientOptionId(value === 'all' ? null : value)}
                    disabled={createMutation.isPending || updateMutation.isPending}
                  >
                    <SelectTrigger className="h-11 min-w-[100px] w-full">
                      <SelectValue placeholder={t('menu.allOptions')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('menu.allOptions')}</SelectItem>
                      {displayOptions?.map((option) => (
                        <SelectItem key={option.id} value={option.id}>
                          {i18n.language === 'ar' ? option.nameAr : option.nameFr}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2 sm:col-span-1">
                  <Label className="text-sm text-transparent hidden lg:block">-</Label>
                  <Button
                    type="button"
                    size="icon"
                    className="w-full lg:w-auto h-11 bg-primary hover:bg-primary/90"
                    onClick={handleAddIngredient}
                    disabled={
                      !selectedIngredientId ||
                      !ingredientQuantity ||
                      parseFloat(ingredientQuantity) <= 0 ||
                      isNaN(parseFloat(ingredientQuantity)) ||
                      createMutation.isPending ||
                      updateMutation.isPending
                    }
                    aria-label="Add ingredient"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {errors['ingredient'] && (
                <p className="text-sm text-destructive">{errors['ingredient']}</p>
              )}

              {/* Selected Ingredients List */}
              {formData['ingredients'].length > 0 && (
                <div className="space-y-3 pt-2">
                  <Label className="text-sm font-medium">{t('menu.selectedIngredients')}</Label>
                  <div className="space-y-2">
                    {formData['ingredients'].map((ing, index) => {
                      const ingredient = ingredients.find((i) => i.id === ing.ingredientId);
                      return (
                        <div
                          key={`${ing.ingredientId}-${ing.menuItemOptionId || 'all'}-${index}`}
                          className="flex items-center justify-between p-3 bg-muted rounded-md hover:bg-muted/80 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            {ingredient && <IngredientIcon ingredient={ingredient} size={24} />}
                            <div className="flex flex-col">
                              <span className="text-sm font-medium">
                                {getIngredientName(ing.ingredientId)}
                              </span>
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-muted-foreground">
                                  {ing.quantity} {t(`inventory.units.${ing.unit}`)}
                                </span>
                                {displayOptions && displayOptions.length > 0 && (
                                  <Badge variant="outline" className="text-xs">
                                    {getOptionName(ing.menuItemOptionId || null)}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() =>
                              handleRemoveIngredient(ing.ingredientId, ing.menuItemOptionId)
                            }
                            disabled={createMutation.isPending || updateMutation.isPending}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {errors['form'] && (
              <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-md">
                {errors['form']}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
              {createMutation.isPending || updateMutation.isPending
                ? t('common.loading')
                : t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
